import { Injectable } from '@angular/core';
import dayjs from '../../utils/dayjs';
import { map, Observable, tap } from 'rxjs';

import { PaymentDetails, PaymentDetailsResponseDto } from '../../services/payments/dto/payment-details-response-dto';
import { PaymentEnabledDto } from '../../services/payments/dto/payment-enabled-dto';
import {
  Item,
  PaymentIntentDto,
} from '../../services/payments/dto/payment-intent-dto';
import { PaymentIntentResponseDto } from '../../services/payments/dto/payment-intent-response-dto';
import { NewSessionWire } from '../../services/payments/models/tuna.model';
import { PaymentApiService } from '../../services/payments/payment-api.service';
import { Intent, PaymentStatus } from '../models/intent';
import { Payment } from '../models/payment';
import { Table } from '../models/table';
import { LocalStorageService } from '../../services/local-storage.service';
import { PaymentValuesService } from './payment-values.service';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  intent?: Intent;
  intentStatus: PaymentStatus;
  paymentUuid: string | null = null;
  lastTotalPaid: number | null = null;

  constructor(
    private paymentApiService: PaymentApiService,
    private localStorageService: LocalStorageService,
    private paymentValuesService: PaymentValuesService,
  ) {
    const intent = this.localStorageService.getItem<Intent>('intent');

    if (intent) {
      this.intent = new Intent({
        idi: 1,
        name: intent.name,
        createdAt: dayjs(intent.createdAt).tz('America/Sao_Paulo').toDate(),
        provider: 'ITAU',
        status: intent.status,
        timeoutMinutes: intent.timeoutInMinutes,
        uuid: intent.uuid,
        qrCode: intent.qrCode,
      });

      this.intent.calculateEndDate();
    }
  }

  getIntent(): Intent | undefined {
    return this.intent;
  }

  getIntentStatus(): PaymentStatus {
    return this.intentStatus;
  }

  setIntentStatus(status: PaymentStatus) {
    this.intentStatus = status;
  }

  getPaymentUuid(): string | null {
    return this.paymentUuid;
  }

  getPaymentsEnabled(idi: number): Observable<PaymentEnabledDto[]> {
    return this.paymentApiService.enabled(idi);
  }

  executePayment(
    table: Table,
    payment: Payment
  ): Observable<PaymentIntentResponseDto> {
    const paymentToSend = new PaymentIntentDto();
    paymentToSend.tableId = table.id;
    paymentToSend.totalPaid = this.paymentValuesService.getTotalToPay(table);
    paymentToSend.payment = payment;
    paymentToSend.type = table.paymentType ?? 'TOTAL';
    const discount = table.getPaymentDiscount();
    if(paymentToSend.type == 'TOTAL' && table.paid == 0 && discount && discount.discountValue > 0) {
      paymentToSend.totalPaid -= this.getDiscountPaymentValue(table);
    }

    if(paymentToSend.totalPaid < 0) {
      paymentToSend.totalPaid = 0.02;
    }

    if (table.paymentType == 'PARTIAL' && table.items?.length) {
      paymentToSend.items = [];
      for (let item of table.items) {
        if (item.groupedItemsId.length && !item.tip && item.qtySelected > 0) {
          let itemsToSend = [];
          for (let groupedItemId of item.groupedItemsId) {
            const itemAdd = new Item();
            itemAdd.id = groupedItemId;
            itemAdd.qty = 1;
            if(itemsToSend.length < item.qtySelected) {
              itemsToSend.push(itemAdd);
            }
          }
          if(itemsToSend.length > 0) {
            paymentToSend.items = paymentToSend.items.concat(itemsToSend);
          }
        } else {
          const itemAdd = new Item();
          itemAdd.id = item.id;
          if (item.tip) {
            // Calcula gorjeta proporcional excluindo itens SERVICE da base
            const proportionalTipValue = table.getProportionalValue(
              item.value,
              table.getTotalSelectedWithoutServiceAndTip()
            );
            itemAdd.overridedValue = proportionalTipValue;
            itemAdd.qty = 1;
          } else {
            itemAdd.qty = item.qtySelected;
          }
          if (itemAdd.qty > 0) {
            paymentToSend.items.push(itemAdd);
          }
        }
      }
    }

    paymentToSend.taxPaid = table.removeTax ?? false;

    if (table.removeTax) {
      paymentToSend.taxValue = table.tax;
    } else if (table.proportionalTax) {
      paymentToSend.taxValue = table.proportionalTax;
    }

    // PARTIAL é o único path em que o front abate voucher%/fidelity%/cashback do
    // totalPaid sem que o back consiga recomputar (TOTAL usa table.remaining cheio
    // do back; CUSTOM é literal). Sem propagar, validatePayment estoura 422.
    if (paymentToSend.type === 'PARTIAL') {
      paymentToSend.voucherDiscount = this.paymentValuesService.getProportionalVoucherDiscount(table);
      paymentToSend.fidelityDiscount = this.paymentValuesService.getProportionalFidelityDiscount(table);
      paymentToSend.cashbackApplied = this.paymentValuesService.getCashbackApplied(table);
    }

    this.lastTotalPaid = paymentToSend.totalPaid;
    this.localStorageService.setItem('intent_total', paymentToSend.totalPaid, LocalStorageService.timeToMs.minutes(30));

    return this.paymentApiService.intent(paymentToSend).pipe(
      tap((res: PaymentIntentResponseDto) => {
        this.intent = new Intent(res.data);
        // Salva o intent com expiração de 30 minutos (tempo padrão para pagamentos)
        this.localStorageService.setItem('intent', this.intent, LocalStorageService.timeToMs.minutes(30));
        this.paymentUuid = this.intent.uuid;
        return res;
      })
    );
  }

  getDiscountPaymentValue(table: Table): number {
    let valueDiscount = 0;
    let discount = table.getPaymentDiscount();
    if(discount) {
      if(discount.discountType == 'PERCENTAGE') {
        // Desconto da forma de pagamento é o ÚLTIMO — depois de manual+acréscimo+
        // cupom+fidelity+cashback. Base = `remaining` (valor final pré-pix).
        const base = Number(table.remaining) || 0;
        valueDiscount = base * (discount.discountValue / 100);
      } else {
        valueDiscount = discount.discountValue;
      }
    }
    return valueDiscount;
  }

  getIntentTotal(): number {
    return this.lastTotalPaid ?? this.localStorageService.getItem<number>('intent_total') ?? 0;
  }

  clearIntent() {
    this.intent = undefined;
    this.lastTotalPaid = null;
    this.localStorageService.removeItem('intent');
    this.localStorageService.removeItem('intent_total');
  }

  cancelPayment(uuid: string): Observable<any> {
    return this.paymentApiService.cancel(uuid).pipe(
      tap(() => {
        this.clearIntent();
      })
    );
  }
  
  getDetails(uuid: string): Observable<PaymentDetails> {
    return this.paymentApiService.details(uuid).pipe(
      tap((res: PaymentDetailsResponseDto) => {
        this.intent = new Intent(res.data);
        // Salva o intent com expiração de 30 minutos
        this.localStorageService.setItem('intent', this.intent, LocalStorageService.timeToMs.minutes(30));
      }),
      map((res: PaymentDetailsResponseDto) => res.data)
    );
  }

  getPaymentStatus(uuid: string): Observable<PaymentStatus> {
    return this.paymentApiService.details(uuid).pipe(
      map((res: PaymentDetailsResponseDto) => res.data.status)
    );
  }

  normalizeSessionToken(response: NewSessionWire): string {
    if (typeof response === 'string') {
      return response;
    }
    if ('token' in response) {
      return response.token;
    }
    if ('sessionId' in response) {
      return response.sessionId;
    }
    
    throw new Error('Resposta inválida ao criar sessão da Tuna');
  }

  createTunaSession(customerId: string, customerEmail: string, idi?: number): Observable<NewSessionWire> {
    return this.paymentApiService.createTunaSession(customerId, customerEmail, idi).pipe(
      map((res: any) => {
        return res.data as NewSessionWire;
      })
    );
  }
}
